// recursive import with userStore & api, but calls are derefed. be careful
import { userStore } from '@stores/User';
import { Packr, unpack } from 'msgpackr';

const API_URL = process.env.API_URL;
if (!API_URL) {
  throw new Error('API_URL is not set');
}
if (API_URL.endsWith('/')) {
  throw new Error('API_URL must not end with a slash');
}

if (
  process.env.API_CONTENT_TYPE !== undefined &&
  !['application/json', 'application/x-msgpack'].includes(process.env.API_CONTENT_TYPE)
) {
  throw new Error(
    'API_CONTENT_TYPE must be either application/json or application/x-msgpack or unset (defaults to application/x-msgpack)',
  );
}

const API_CONTENT_TYPE = process.env.API_CONTENT_TYPE || 'application/x-msgpack';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly data?: Record<string, any>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get shouldInvalidateToken() {
    return this.status === 401;
  }
}

/**
 * HTTP 429. Lives here rather than in a page folder because both the auth
 * forms and the status pollers need it.
 */
export const isRateLimitError = (e: unknown): boolean => e instanceof ApiError && e.status === 429;

/** HTTP 404 — the only error that proves a media is actually gone. */
export const isNotFoundError = (e: unknown): boolean => e instanceof ApiError && e.status === 404;

type ErrorApiResponse = {
  success: false;
  error: {
    statusCode: number;
    error: string;
    message: string;
    code: string;
    data?: Record<string, any>;
  };
};

type SuccessApiResponse<T> = {
  success: true;
  data: T;
  token?: string;
};

export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  body?: object | RequestInit['body'];
};

export const apiPathToUrl = (path: string) => `${API_URL}${path}`;

/**
 * A URL a media element (`<video>`, `<img>`) can load: those cannot send an
 * Authorization header, so the session JWT travels as the `access_token`
 * query parameter, which the routes that serve files accept. Null without a
 * session.
 *
 * FIXME(token-in-url): the whole-account session token ends up in a URL
 * that every proxy and access log on the path records, where a header would
 * not. Acceptable once: a media element has no other way to authenticate,
 * the API already accepts the parameter for the HLS fallback and the Atlas
 * thumbnails, and the alternative for a render (mint a signature with
 * `GET /rendering/compilation/v2/{id}/share`, play `/share/video?sig&exp`)
 * costs a request per render and hands out a week-long public link. Retire
 * by teaching `public/service-worker.js` to re-issue media requests to the
 * API origin with the `Authorization` header (it already intercepts fetches
 * and has a message channel to receive the token; the query parameter stays
 * only as the fallback where no worker runs), or when the API issues a
 * short-lived read-only media credential: swap it here and every caller
 * follows.
 */
export const apiPathToUrlWithToken = (path: string, token: string | null): string | null => {
  if (!token) return null;
  const separator = path.includes('?') ? '&' : '?';
  return apiPathToUrl(`${path}${separator}access_token=${encodeURIComponent(token)}`);
};

// https://github.com/kriszyp/msgpackr/pull/77
// skipValues need useRecords to be false
// but typescript definition is missing skipValues
// @ts-ignore
const packr = new Packr({ useRecords: false, skipValues: [undefined] });

const getParsedResponse = async (response: Response) => {
  const responseContentType = response.headers.get('content-type');
  if (!responseContentType) {
    throw new Error('Response has no content type');
  }
  if (responseContentType.includes('application/json')) return response.json();
  if (responseContentType.includes('application/x-msgpack'))
    return unpack(new Uint8Array(await response.arrayBuffer()));
  throw new Error('Response has unsupported content type');
};

// todo: in TS 5.8.0, refactor this monstrosity
export async function api<T>(
  path: string,
  options?: ApiRequestInit,
  parseResponse?: true,
): Promise<SuccessApiResponse<T>['data']>;
export async function api(
  path: string,
  options?: ApiRequestInit,
  parseResponse?: false,
): Promise<Response>;
export async function api<
  T,
  Parse extends true | false = true,
  // this shhould be unnecessary in TS 5.8.0
  ReturnData extends Parse extends true
    ? SuccessApiResponse<T>['data']
    : Response = Parse extends true ? SuccessApiResponse<T>['data'] : Response,
>(
  path: string,
  options?: ApiRequestInit,
  parseResponse: Parse = true as Parse,
): Promise<ReturnData> {
  if (!options) {
    options = {};
  }
  const forceJson = localStorage.getItem('debug.forceJsonApi') === 'true';
  const contentType = forceJson ? 'application/json' : API_CONTENT_TYPE;
  if (options.body && typeof options.body === 'object') {
    if (contentType === 'application/json') {
      options.body = JSON.stringify(options.body);
    }
    if (contentType === 'application/x-msgpack') {
      options.body = packr.pack(options.body);
    }
  }
  options.headers = {
    ...options.headers,
    'content-type': contentType,
    accept: contentType,
  };
  // Include credentials in the request so that the server can set cookies
  if (!options.credentials) {
    options.credentials = 'include';
  }

  const response = await fetch(apiPathToUrl(path), options as RequestInit);

  if (response.status >= 400) {
    const data = (await getParsedResponse(response)) as ErrorApiResponse;
    throw new ApiError(data.error.code, data.error.message, response.status, data.error.data);
  }

  if (parseResponse) {
    const data = (await getParsedResponse(
      response,
    )) as SuccessApiResponse<T> as SuccessApiResponse<T>;
    if (data.token) {
      userStore.setToken(data.token);
    }

    // this should be unnecessary in TS 5.8.0
    return data.data as unknown as ReturnData;
  }
  // this should be unnecessary in TS 5.8.0
  return response as unknown as ReturnData;
}

/**
 * Makes an authenticated API request that returns a Server-Sent Events (SSE) stream.
 *
 * This function is specifically designed for SSE endpoints and differs from the regular
 * `api()` function in several important ways:
 *
 * **Why JSON instead of msgpack for the request body:**
 * SSE is a text-based protocol. While our regular API uses msgpack for efficiency,
 * the SSE endpoint returns a text/event-stream response. When msgpack Accept header
 * is sent, the backend incorrectly serializes internal Node.js stream objects instead
 * of the actual SSE events. Using JSON for both request and response ensures proper
 * SSE streaming behavior.
 *
 * **Why this returns a raw Response:**
 * SSE streams need to be consumed incrementally using a ReadableStream reader.
 * The caller is responsible for parsing the SSE events from the response body.
 * This is fundamentally different from regular API calls where we parse the entire
 * response at once.
 *
 * **Error handling:**
 * Errors are thrown as ApiError (consistent with the regular api() function) so that
 * callers can use the same error handling patterns, including token invalidation on 401.
 *
 * @param path - The API endpoint path (e.g., '/library/media/external/unfurl/sse')
 * @param options - Request options (method, body, signal, etc.)
 * @returns The raw Response object for streaming
 * @throws {ApiError} If the request fails (status >= 400)
 *
 * @example
 * const response = await apiSSE('/library/media/external/unfurl/sse', {
 *   method: 'POST',
 *   body: { url: 'https://youtube.com/watch?v=...' },
 *   signal: abortController.signal,
 * });
 * const reader = response.body.getReader();
 * // ... process SSE events from the stream
 */
export async function apiSSE(path: string, options?: ApiRequestInit): Promise<Response> {
  if (!options) {
    options = {};
  }

  // Get auth token - SSE endpoints require authentication
  const token = userStore.token;
  if (!token) {
    userStore.handleSessionExpired();
    throw new ApiError('auth.no_token', 'No authentication token available', 401);
  }

  // SSE requires JSON encoding for the request body.
  // Unlike regular API calls that can use msgpack, SSE is a text-based protocol
  // and the backend expects JSON when streaming SSE responses.
  if (options.body && typeof options.body === 'object') {
    options.body = JSON.stringify(options.body);
  }

  options.headers = {
    ...options.headers,
    // Request body is JSON
    'Content-Type': 'application/json',
    // Explicitly request SSE format - this tells the backend we want a streaming
    // response rather than a single JSON/msgpack payload
    Accept: 'text/event-stream',
    // Auth token for authenticated endpoints
    Authorization: `Bearer ${token}`,
  };

  // Include credentials for cookie-based auth (consistent with regular api())
  if (!options.credentials) {
    options.credentials = 'include';
  }

  const response = await fetch(apiPathToUrl(path), options as RequestInit);

  // Handle errors consistently with the regular api() function
  if (response.status >= 400) {
    // For error responses, the backend returns JSON (not SSE), so we parse it
    // to extract the detailed error message
    let errorCode = 'api.error';
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorData: Record<string, any> | undefined;

    try {
      const parsed = (await response.json()) as ErrorApiResponse;
      if (parsed.error) {
        errorCode = parsed.error.code;
        errorMessage = parsed.error.message;
        errorData = parsed.error.data;
      }
    } catch {
      // JSON parse failed, use default HTTP error message
    }

    const error = new ApiError(errorCode, errorMessage, response.status, errorData);
    if (error.shouldInvalidateToken) {
      userStore.handleSessionExpired();
    }
    throw error;
  }

  // Return the raw response for the caller to stream
  return response;
}
